import { Button } from '@massilia/ui'
import { type ErrorComponentProps, Link } from '@tanstack/react-router'
import { AxiosError } from 'axios'
import { FileQuestion, Search } from 'lucide-react'

import { ClientErrorState } from '@/components/errors/client-error-state'
import { resolveClientErrorType } from '@/lib/client-error'

type ReservationErrorProps = ErrorComponentProps | { error?: Error | null }

/**
 * Frontière des deux routes de devis (`_blue_header/reservation.$estimateResponseId`
 * et `confirmation-reservation.$reservationId`).
 *
 * Elle recopiait `error.response.data.message` sans passer par
 * `isClientReadable` : le seul écran du site où un texte écrit pour un
 * développeur pouvait s'afficher tel quel. Le catalogue partagé s'en charge
 * désormais, et le 404 — un devis expiré ou supprimé, de loin le cas le plus
 * fréquent ici — garde son propre écran, qui n'a rien d'une panne.
 */
export function ReservationError(props: ReservationErrorProps) {
  const error = props.error

  if (!error || isMissingEstimate(error)) {
    return (
      <ReservationErrorFrame>
        <div className="flex flex-col items-center gap-6 py-12 text-center" role="alert">
          <div className="flex size-16 items-center justify-center rounded-full bg-warning-subtle">
            <FileQuestion className="size-8 text-warning-foreground" />
          </div>
          <div className="flex max-w-md flex-col gap-2">
            <h1 className="text-xl font-semibold text-gray-light-900">Devis introuvable</h1>
            <p className="text-sm leading-relaxed text-gray-light-600">
              Ce devis n’existe pas ou n’est plus disponible. Les devis ont une durée de validité
              limitée : contactez-nous si vous souhaitez le rouvrir, ou lancez une nouvelle
              recherche.
            </p>
          </div>
          <BackToSearch />
        </div>
      </ReservationErrorFrame>
    )
  }

  // Sorti dans une variable : cf. `booking-error.tsx`, les accolades d'un élément
  // passé directement en attribut se font retirer par le linter.
  const actions = <BackToSearch />

  return (
    <ReservationErrorFrame>
      <ClientErrorState
        actions={actions}
        error={error}
        type={resolveClientErrorType(error)}
        variant="page"
      />
    </ReservationErrorFrame>
  )
}

function BackToSearch() {
  return (
    <Button asChild className="bg-brand-600 hover:bg-brand-700">
      <Link to="/recherche">
        <Search className="size-4" />
        Lancer une recherche
      </Link>
    </Button>
  )
}

/** Le devis n'existe pas — un 404, ou une erreur sans réponse HTTP exploitable. */
function isMissingEstimate(error: Error): boolean {
  return error instanceof AxiosError && error.response?.status === 404
}

function ReservationErrorFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-1 items-center justify-center px-4">
      <div className="w-full max-w-md">{children}</div>
    </div>
  )
}
